/**
 * Màn **Bài học** — nơi nhìn thấy vòng học có thật sự khép kín hay không.
 *
 * Ô số quan trọng nhất ở đây **không phải** số luật, mà là **số lần từ chối chưa sinh luật**.
 * Trước đợt 14 con số đó là 1 và không màn hình nào nói ra: bảng `lesson` đứng ở 0 trong khi
 * `reviewaction` đã có 5 dòng, và một bảng luật rỗng vì hỏng trông **y hệt** một bảng luật rỗng
 * vì chưa ai từ chối gì. Đó đúng là luật *"chưa đo được ≠ bằng 0"* (`CLAUDE.md` §5.14) áp vào một
 * trường hợp cụ thể.
 *
 * Và một thứ nữa cố ý hiện ra: **lý do gốc** đã đẻ ra mỗi luật. Không có nó thì một luật sai chỉ
 * còn cách xoá đi, thay vì sửa lại cho đúng ý người đã từ chối.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type Luat } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import { gioVN } from "../lib/nhan.ts";
import {
  Bao,
  Chip,
  DangTai,
  DanhSach,
  DauTrang,
  Hang,
  Nut,
  Doc,
  LuoiDoc,
  Rong,
  The,
  lopO,
} from "../thanh-phan/giao-dien.tsx";
import { useToast } from "../thanh-phan/toast.tsx";

type Loc = "tat-ca" | "ARTICLE" | "IMAGE";

export function BaiHoc() {
  const qc = useQueryClient();
  const toast = useToast();
  const [loc, datLoc] = useState<Loc>("tat-ca");
  const [chiDangBat, datChiDangBat] = useState(false);
  const [suaId, datSuaId] = useState<string | null>(null);
  const [banSua, datBanSua] = useState("");

  const { data, isPending, error } = useQuery({
    queryKey: [...khoa.baiHoc, loc, chiDangBat],
    queryFn: () =>
      api.baiHoc({ apDungCho: loc === "tat-ca" ? undefined : loc, chiDangBat }),
  });

  const lamMoi = () => {
    void qc.invalidateQueries({ queryKey: khoa.baiHoc });
    void qc.invalidateQueries({ queryKey: khoa.tongQuan });
  };

  const doiCo = useMutation({
    mutationFn: (v: { id: string; truong: "ghim" | "bat"; bat: boolean }) =>
      v.truong === "ghim" ? api.baiHocGhim(v.id, v.bat) : api.baiHocBat(v.id, v.bat),
    onSuccess: lamMoi,
    // Ghim/tắt một luật là thao tác **im lặng khi thành công** — hàng đổi ngay trước mắt, đó là
    // phản hồi rồi. Chỉ lỗi mới cần nói ra.
    onError: (loi: Error) => toast.loi("Không đổi được cờ", loi.message),
  });

  const sua = useMutation({
    mutationFn: (v: { id: string; noiDung: string }) => api.baiHocSua(v.id, v.noiDung),
    onSuccess: () => {
      datSuaId(null);
      datBanSua("");
      lamMoi();
      toast.on("Đã sửa luật");
    },
    onError: (loi: Error) => toast.loi("Không sửa được luật", loi.message),
  });

  const chayBu = useMutation({
    mutationFn: () => api.baiHocChayBu(20),
    onSuccess: (kq) => {
      toast.on(
        `Đã xếp ${kq.daXep} job sinh luật`,
        `Còn ${kq.conLai} lần từ chối chưa có luật. Job chạy nền — bấm Làm mới sau ít giây.`,
      );
      lamMoi();
    },
    onError: (loi: Error) => toast.loi("Chạy bù không được", loi.message),
  });

  if (error) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Bài học" />
        <Bao muc="loi" tieuDe="Không tải được">
          {error.message}
        </Bao>
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Bài học" />
        <DangTai />
      </div>
    );
  }

  const { luat, soTuChoiChuaSinhLuat } = data;
  const soDangBat = luat.filter((l) => l.dangBat).length;
  const soGhim = luat.filter((l) => l.ghim).length;

  return (
    <div className="min-w-0 space-y-5">
      <DauTrang
        tieuDe="Bài học"
        moTa="Mỗi lần từ chối sinh đúng một luật. Chỉ AI tạo prompt đọc chúng — AI viết bài không đọc."
      >
        <Nut kieu="vien" onClick={lamMoi}>
          Làm mới
        </Nut>
      </DauTrang>

      <LuoiDoc cot={4}>
        <Doc nhan="Luật đang bật" giaTri={soDangBat} />
        <Doc nhan="Đã ghim" giaTri={soGhim} />
        <Doc nhan="Tổng luật" giaTri={luat.length} />
        {/*
          Ô đáng nhìn nhất màn này. `canAnh` bật viền cam khi khác 0 — cam mang đúng một nghĩa
          trong cả app, và "vòng học đang đứt" chính là nghĩa đó.
        */}
        <Doc
          nhan="Từ chối chưa sinh luật"
          giaTri={soTuChoiChuaSinhLuat}
          canAnh={soTuChoiChuaSinhLuat > 0}
          sacThai={soTuChoiChuaSinhLuat === 0 ? "tot" : undefined}
        />
      </LuoiDoc>

      {soTuChoiChuaSinhLuat > 0 && (
        <Bao muc="canh" tieuDe={`${soTuChoiChuaSinhLuat} lần từ chối chưa sinh được luật`}>
          Vòng học đang đứt ở những lần đó — bài sau sẽ lặp lại đúng lỗi vừa bị từ chối. Bấm nút
          dưới để sinh bù. Mỗi lượt là một lời gọi model có tính tiền.
          <div className="mt-2">
            <Nut kieu="chinh" dangChay={chayBu.isPending} onClick={() => chayBu.mutate()}>
              Chạy bù (tối đa 20)
            </Nut>
          </div>
        </Bao>
      )}

      {/* Kết quả "chạy bù" và mọi lỗi đi bằng toast — xem các `onSuccess`/`onError` ở trên. */}

      <The>
        <div className="flex flex-wrap items-center gap-2">
          {(["tat-ca", "ARTICLE", "IMAGE"] as Loc[]).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => datLoc(v)}
              className={
                loc === v
                  ? "rounded-btn bg-canh-500/12 px-2.5 py-1 text-2xs font-semibold text-canh-600 ring-1 ring-canh-200"
                  : "rounded-btn bg-muc-0 px-2.5 py-1 text-2xs text-muc-600 ring-1 ring-muc-300 hover:bg-muc-100"
              }
            >
              {v === "tat-ca" ? "Tất cả" : v === "ARTICLE" ? "Luật cho bài" : "Luật cho ảnh"}
            </button>
          ))}
          <label className="ml-2 flex items-center gap-1.5 text-2xs text-muc-600">
            <input
              type="checkbox"
              checked={chiDangBat}
              onChange={(e) => datChiDangBat(e.target.checked)}
            />
            chỉ luật đang bật
          </label>
        </div>
        <p className="mt-2 text-2xs text-muc-500">
          <strong>ARTICLE</strong> và <strong>IMAGE</strong> không trộn vào nhau — một luật về ảnh
          lọt vào prompt viết bài là một câu vô nghĩa nằm giữa đề bài.
        </p>
      </The>

      {luat.length === 0 ? (
        <Rong tieuDe="Chưa có luật nào">
          {soTuChoiChuaSinhLuat > 0
            ? "Nhưng đã có lần từ chối — nghĩa là vòng học đang đứt, không phải chưa ai từ chối gì."
            : "Chưa ai từ chối bài nào, nên chưa có gì để học. Đây là trạng thái đúng, không phải lỗi."}
        </Rong>
      ) : (
        <DanhSach>
          {luat.map((l: Luat) => (
            <Hang key={l._id}>
              <div className="min-w-0 flex-1 px-4 py-3">
                {suaId === l._id ? (
                  <div className="space-y-2">
                    <textarea
                      value={banSua}
                      onChange={(e) => datBanSua(e.target.value)}
                      rows={3}
                      className={lopO}
                    />
                    <div className="flex gap-2">
                      <Nut
                        kieu="chinh"
                        dangChay={sua.isPending}
                        disabled={banSua.trim().length < 5}
                        onClick={() => sua.mutate({ id: l._id, noiDung: banSua })}
                      >
                        Lưu
                      </Nut>
                      <Nut kieu="vien" onClick={() => datSuaId(null)}>
                        Huỷ
                      </Nut>
                    </div>
                  </div>
                ) : (
                  <p
                    className={
                      l.dangBat
                        ? "min-w-0 break-words text-sm text-muc-900"
                        : "min-w-0 break-words text-sm text-muc-400 line-through"
                    }
                  >
                    {l.noiDung}
                  </p>
                )}

                <div className="mt-2 flex min-w-0 flex-wrap items-center gap-1.5">
                  <Chip muc={l.ápDungCho === "IMAGE" ? "canh" : "thuong"}>{l.ápDungCho}</Chip>
                  <Chip muc="thuong">{l.siteSlug ?? "mọi site"}</Chip>
                  {l.ghim && <Chip muc="on">đã ghim</Chip>}
                  {!l.dangBat && <Chip muc="loi">đã tắt</Chip>}
                  <Chip muc="thuong">{l.nguoiGhi === "may" ? "máy viết" : l.nguoiGhi}</Chip>
                  {l.tags.map((t) => (
                    <Chip key={t} muc="thuong">
                      {t}
                    </Chip>
                  ))}
                  <span className="text-2xs text-muc-400">{gioVN(l.createdAt)}</span>
                </div>

                {l.lyDoGoc !== null && (
                  <p className="mt-1.5 min-w-0 break-words text-2xs text-muc-500">
                    <span className="text-muc-400">Sinh từ lý do:</span> {l.lyDoGoc}
                  </p>
                )}

                <div className="mt-2.5 flex flex-wrap gap-2">
                  <Nut
                    kieu="vien"
                    // Chỉ nút vừa bấm mới quay. `isPending` một mình làm mọi hàng trong danh
                    // sách cùng quay, và người bấm mất dấu chính cái mình vừa chạm.
                    dangChay={
                      doiCo.isPending &&
                      doiCo.variables?.id === l._id &&
                      doiCo.variables?.truong === "ghim"
                    }
                    onClick={() => doiCo.mutate({ id: l._id, truong: "ghim", bat: !l.ghim })}
                  >
                    {l.ghim ? "Bỏ ghim" : "Ghim"}
                  </Nut>
                  <Nut
                    kieu="vien"
                    dangChay={
                      doiCo.isPending &&
                      doiCo.variables?.id === l._id &&
                      doiCo.variables?.truong === "bat"
                    }
                    onClick={() => doiCo.mutate({ id: l._id, truong: "bat", bat: !l.dangBat })}
                  >
                    {l.dangBat ? "Tắt" : "Bật lại"}
                  </Nut>
                  <Nut
                    kieu="vien"
                    onClick={() => {
                      datSuaId(l._id);
                      datBanSua(l.noiDung);
                    }}
                  >
                    Sửa
                  </Nut>
                </div>
              </div>
            </Hang>
          ))}
        </DanhSach>
      )}

      {(doiCo.error || sua.error) && (
        <Bao muc="loi" tieuDe="Không lưu được">
          {(doiCo.error ?? sua.error)?.message}
        </Bao>
      )}

      <p className="text-2xs text-muc-500">
        Luật bị gộp hoặc không còn đúng thì <strong>tắt, không xoá</strong> — xoá là mất dấu vết vì
        sao nó từng tồn tại, và lần gộp sau nó được sinh lại từ đầu.
      </p>
    </div>
  );
}
